/**
 * Chart colour-scheme registry for the dashboard designer.
 *
 * Theme / theme-alt reorder the brand `--color-chart-1..8` tokens.
 * Named schemes map to `--color-chart-scheme-<id>-1..8` tokens defined in
 * `apps/web/src/theme.css` (Power BI built-ins + common categorical sets).
 * Keep ChartCard and the Configure preview on this single resolver.
 */

export const CHART_COLOR_SCHEME_OPTIONS = [
  { value: "theme", label: "Theme Palette" },
  { value: "theme-alt", label: "Theme Palette (alt order)" },
  { value: "pbi-default", label: "Default" },
  { value: "pbi-electric", label: "Electric" },
  { value: "pbi-sunset", label: "Sunset" },
  { value: "pbi-colorblind", label: "Colorblind Safe" },
  { value: "pbi-city-park", label: "City Park" },
  { value: "pbi-classroom", label: "Classroom" },
  { value: "pbi-high-contrast", label: "High Contrast" },
  { value: "pbi-executive", label: "Executive" },
  { value: "tableau-10", label: "Tableau 10" },
  { value: "okabe-ito", label: "Okabe–Ito (colorblind)" },
] as const;

export type DashboardChartColorScheme = (typeof CHART_COLOR_SCHEME_OPTIONS)[number]["value"];

const NAMED_SCHEMES = new Set<DashboardChartColorScheme>(
  CHART_COLOR_SCHEME_OPTIONS.map((o) => o.value).filter((v) => v !== "theme" && v !== "theme-alt"),
);

function themeBase(): string[] {
  return Array.from({ length: 8 }, (_, i) => `var(--color-chart-${i + 1})`);
}

function namedSchemeColors(scheme: DashboardChartColorScheme): string[] {
  return Array.from({ length: 8 }, (_, i) => `var(--color-chart-scheme-${scheme}-${i + 1})`);
}

/** Resolve CSS colour tokens for a chart colour scheme. */
export function resolveSchemeColors(scheme: DashboardChartColorScheme | undefined): string[] {
  const key = scheme ?? "pbi-electric";
  if (key === "theme-alt") {
    const base = themeBase();
    // Higher-contrast stage order: brand, green, blue, amber, deep brand, red, soft brand, muted.
    return [base[0], base[2], base[5], base[3], base[4], base[6], base[1], base[7]];
  }
  if (NAMED_SCHEMES.has(key)) {
    return namedSchemeColors(key);
  }
  return themeBase();
}
